import React, { useState } from 'react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import { media } from '@atlaskit/primitives/responsive';

// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { type SidebarProps } from '../types';

import { Body } from './body';
import { EmptyFooter } from './empty-footer';
import { Footer } from './footer';
import { Header } from './header';

const containerStyles = xcss({
	height: '100%',
	width: '100%',
	minWidth: '320px',
	maxWidth: '504px',
	display: 'flex',
	flexDirection: 'column',
});

const containerWithExpandedErrorStyles = xcss({
	maxWidth: 'initial',
});

const containerExpandedStyles = xcss({
	maxWidth: '320px',

	[media.above.lg]: {
		maxWidth: '400px',
	},

	[media.above.xl]: {
		maxWidth: '486px',
	},
});

type MainSectionProps = Pick<
	SidebarProps,
	| 'title'
	| 'titleComponent'
	| 'mainContent'
	| 'isLoading'
	| 'hasError'
	| 'errorHeader'
	| 'errorDescription'
	| 'footerButtons'
	| 'onClose'
	| 'isExpanded'
> & { children?: React.ReactNode; closeButtonRef?: React.RefObject<HTMLButtonElement> };

export const MainSection = (props: MainSectionProps): React.JSX.Element => {
	const {
		title,
		titleComponent,
		isLoading = false,
		hasError = false,
		isExpanded = false,
		errorHeader,
		errorDescription,
		footerButtons = [],
		children,
		onClose,
		closeButtonRef,
	} = props;

	const [showHeaderDropShadow, setShowHeaderDropShadow] = useState(false);
	const [showFooterDropShadow, setShowFooterDropShadow] = useState(false);

	const handleOnBodyScrolled = (props: { showBottomShadow: boolean; showTopShadow: boolean }) => {
		setShowHeaderDropShadow(props.showTopShadow);
		setShowFooterDropShadow(props.showBottomShadow);
	};

	return (
		<Box
			xcss={[
				containerStyles,
				hasError && isExpanded && containerWithExpandedErrorStyles,
				!hasError && isExpanded && containerExpandedStyles,
			]}
		>
			<Header
				title={title}
				titleComponent={titleComponent}
				hasError={hasError}
				onClose={onClose}
				showDropShadow={showHeaderDropShadow}
				closeButtonRef={closeButtonRef}
			/>
			<Body
				onBodyScrolled={handleOnBodyScrolled}
				isLoading={isLoading}
				hasError={hasError}
				errorHeader={errorHeader}
				errorDescription={errorDescription}
				isFooterShown={footerButtons.length > 0}
			>
				{children}
			</Body>
			{hasError ? (
				<EmptyFooter />
			) : (
				<Footer buttons={footerButtons} showDropShadow={showFooterDropShadow} />
			)}
		</Box>
	);
};
